import React from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import Logo from '../ui/Logo';

const NAV_BREAK = '1180px';
const TABLET_BREAK = '768px';

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Shell = styled.div`
  position: fixed;
  inset: 0;
  z-index: 999;
  display: flex;
  align-items: ${(p) => (p.$tablet ? 'flex-start' : 'stretch')};
  justify-content: center;
  padding: ${(p) =>
    p.$tablet ? 'max(88px, 10vh) 24px 24px' : '0'};
  pointer-events: ${(p) => (p.$open ? 'auto' : 'none')};
  visibility: ${(p) => (p.$open ? 'visible' : 'hidden')};
  transition: visibility 0s ${(p) => (p.$open ? '0s' : '0.4s')};
`;

const Backdrop = styled.button`
  position: absolute;
  inset: 0;
  border: none;
  padding: 0;
  cursor: pointer;
  background: rgba(7, 17, 31, 0.25);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  opacity: ${(p) => (p.$open ? 1 : 0)};
  transition: opacity 0.35s ease;
`;

const Panel = styled.aside`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: 100dvh;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.78);
  backdrop-filter: blur(32px) saturate(180%);
  -webkit-backdrop-filter: blur(32px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.85);
  box-shadow:
    0 32px 96px rgba(7, 17, 31, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.95);

  @media (max-width: ${TABLET_BREAK}) {
    height: 100dvh;
    max-height: 100vh;
    border-radius: 0;
    border-left: none;
    border-right: none;
    transform: translateY(${(p) => (p.$open ? '0' : '100%')});
    transition: transform 0.45s cubic-bezier(0.22, 1, 0.36, 1);
  }

  @media (min-width: ${TABLET_BREAK}) and (max-width: ${NAV_BREAK}) {
    width: min(560px, 94vw);
    max-height: min(680px, 88dvh);
    border-radius: 28px;
    opacity: ${(p) => (p.$open ? 1 : 0)};
    transform: translateY(${(p) => (p.$open ? '0' : '20px')}) scale(${(p) => (p.$open ? 1 : 0.95)});
    transition: opacity 0.4s ease, transform 0.45s cubic-bezier(0.22, 1, 0.36, 1);
  }
`;

const PanelTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 22px 24px;
  border-bottom: 1px solid rgba(14, 107, 60, 0.1);
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.3);
`;

const CloseBtn = styled.button`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.9);
  background: rgba(255, 255, 255, 0.65);
  backdrop-filter: blur(16px);
  color: #475569;
  font-size: 1.2rem;
  cursor: pointer;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
  box-shadow: 0 4px 16px rgba(7, 17, 31, 0.06);

  &:hover {
    background: rgba(14, 107, 60, 0.15);
    color: #0e6b3c;
    transform: scale(1.08);
    box-shadow: 0 6px 20px rgba(14, 107, 60, 0.15);
  }
`;

const Nav = styled.nav`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  -webkit-overflow-scrolling: touch;

  @media (min-width: ${TABLET_BREAK}) {
    padding: 28px 32px;
  }
`;

const NavList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;

  @media (min-width: ${TABLET_BREAK}) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
`;

const NavLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;
  border-radius: 18px;
  text-decoration: none;
  font-weight: 700;
  font-size: 1.1rem;
  color: ${(p) => (p.$active ? '#0e6b3c' : '#1e293b')};
  background: ${(p) =>
    p.$active ? 'rgba(14, 107, 60, 0.15)' : 'rgba(255, 255, 255, 0.55)'};
  border: 1px solid
    ${(p) => (p.$active ? 'rgba(14, 107, 60, 0.3)' : 'rgba(255, 255, 255, 0.75)')};
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  box-shadow: 0 6px 20px rgba(7, 17, 31, 0.06);
  opacity: ${(p) => (p.$show ? 1 : 0)};
  animation: ${(p) => (p.$show ? fadeUp : 'none')} 0.5s cubic-bezier(0.22, 1, 0.36, 1) both;
  animation-delay: ${(p) => p.$delay}ms;
  transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 32px rgba(14, 107, 60, 0.18);
    background: rgba(255, 255, 255, 0.8);
    border-color: rgba(14, 107, 60, 0.2);
  }

  i {
    font-size: 0.75rem;
    opacity: 0.5;
  }
`;

const PanelFoot = styled.div`
  flex-shrink: 0;
  padding: 20px 24px max(24px, env(safe-area-inset-bottom));
  border-top: 1px solid rgba(14, 107, 60, 0.1);
  background: rgba(255, 255, 255, 0.3);
`;

const CTA = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  padding: 18px 28px;
  border-radius: 999px;
  font-weight: 700;
  font-size: 1.05rem;
  font-family: var(--font-display);
  text-decoration: none;
  color: #fff;
  background: linear-gradient(
    135deg,
    rgba(14, 107, 60, 0.96) 0%,
    rgba(10, 90, 50, 0.98) 100%
  );
  border: 1px solid rgba(255, 255, 255, 0.35);
  box-shadow: 0 16px 40px rgba(14, 107, 60, 0.35);
  backdrop-filter: blur(12px);
  transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 20px 48px rgba(14, 107, 60, 0.45);
  }
`;

const useIsTabletMenu = () => {
  const [tablet, setTablet] = React.useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia(`(min-width: ${TABLET_BREAK}) and (max-width: ${NAV_BREAK})`).matches
  );

  React.useEffect(() => {
    const mq = window.matchMedia(
      `(min-width: ${TABLET_BREAK}) and (max-width: ${NAV_BREAK})`
    );
    const fn = () => setTablet(mq.matches);
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, []);

  return tablet;
};

const MobileGlassMenu = ({ open, onClose, items, isActive }) => {
  const isTablet = useIsTabletMenu();

  return (
    <Shell
      id="mobile-navigation"
      $open={open}
      $tablet={isTablet}
      role="dialog"
      aria-modal="true"
      aria-label="Navigation"
      aria-hidden={!open}
    >
      <Backdrop type="button" $open={open} onClick={onClose} aria-label="Close menu" />
      <Panel $open={open}>
        <PanelTop>
          <Logo size="drawer" maxWidth="400px" />
          <CloseBtn type="button" onClick={onClose} aria-label="Close menu">
            <i className="fas fa-times" aria-hidden />
          </CloseBtn>
        </PanelTop>

        <Nav>
          <NavList>
            {items.map((item, i) => (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  onClick={onClose}
                  $active={isActive(item.path)}
                  $show={open}
                  $delay={60 + i * 45}
                >
                  {item.label}
                  <i className="fas fa-arrow-right" aria-hidden />
                </NavLink>
              </li>
            ))}
          </NavList>
        </Nav>

        <PanelFoot>
          <CTA to="/contact" onClick={onClose}>
            Get Free Consultation
            <i className="fas fa-arrow-right" aria-hidden />
          </CTA>
        </PanelFoot>
      </Panel>
    </Shell>
  );
};

export default MobileGlassMenu;
